import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Controller } from 'react-hook-form';
import Select from 'react-select';
import Api from '../../../api/global.service';
import BlockUi from 'react-block-ui';

const emptyFunction = () => {};

// The input-group container (sirio.css) provides the border and border-radius.
// The control itself must have no border, matching how .input-group .form-control
// strips the border from native selects.
const selectStyles = {
  control: base => ({
    ...base,
    border: 'none',
    borderRadius: '0px',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    fontFamily: 'Open Sans',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '14px',
    minHeight: '36px',
    '&:hover': { border: 'none' },
  }),
  valueContainer: base => ({
    ...base,
    padding: '4px 0px',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({
    ...base,
    padding: '4px 8px',
    color: '#777771',
  }),
  clearIndicator: base => ({
    ...base,
    padding: '4px 4px',
    color: '#777771',
  }),
  placeholder: base => ({
    ...base,
    fontSize: '14px',
    color: '#6c757d',
    marginLeft: '2px',
  }),
  singleValue: base => ({
    ...base,
    fontSize: '14px',
    color: '#495057',
    marginLeft: '2px',
  }),
  input: base => ({
    ...base,
    fontSize: '14px',
    fontFamily: 'Open Sans',
    color: '#495057',
    margin: '0',
    padding: '0',
  }),
  option: (base, state) => ({
    ...base,
    fontSize: '14px',
    fontFamily: 'Open Sans',
    backgroundColor: state.isSelected ? '#789D16' : state.isFocused ? '#f8f9fa' : 'white',
    color: state.isSelected ? 'white' : '#212529',
    cursor: 'pointer',
    ':active': {
      backgroundColor: '#4A6700',
      color: 'white',
    },
  }),
  menu: base => ({
    ...base,
    borderRadius: '4px',
    zIndex: 9999,
    marginTop: '2px',
  }),
  // Menu rendered via menuPortalTarget — positioned absolutely inside the
  // input-group (which has position:relative), spanning its full width.
  menuPortal: base => ({
    ...base,
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    zIndex: 9999,
    width: 'auto',
    marginTop: '2px',
  }),
};

const FormSelectApiSearchable = ({
  url,
  id,
  label,
  name,
  error,
  keyArray,
  labelArray,
  control,
  onChange: onChangeProp = emptyFunction,
  onFinish = emptyFunction,
  placeholder = '--- SELECCIONE ---',
  filter = false,
  column = false,
  value = false,
  isClearable = false,
  pinToTop = null,
}) => {
  const [options, setOptions] = useState([]);
  const [blocking, setBlocking] = useState(true);
  // Callback ref so react re-renders once the input-group DOM node is available,
  // allowing menuPortalTarget to be set before the user first opens the menu.
  const [containerEl, setContainerEl] = useState(null);
  const onFinishRef = useRef(onFinish);
  const selectRef = useRef();
  useEffect(() => { onFinishRef.current = onFinish; });

  const fetchData = useCallback(async () => {
    if (filter && value === 0) {
      setBlocking(false);
      setOptions([]);
      return;
    }
    try {
      setBlocking(true);
      const response = filter
        ? await Api.search(url, { filter: [{ column, operator: '=', value }] })
        : await Api.getAll(url);
      const mapped = response.data.map(item => ({
        value: item[keyArray],
        label: item[labelArray],
      }));
      const sorted = pinToTop
        ? [...mapped.filter(pinToTop), ...mapped.filter(o => !pinToTop(o))]
        : mapped;
      ReactDOM.unstable_batchedUpdates(() => {
        setBlocking(false);
        setOptions(sorted);
      });
      onFinishRef.current(response.data);
    } catch {
      ReactDOM.unstable_batchedUpdates(() => {
        setBlocking(false);
        setOptions([]);
      });
      onFinishRef.current([]);
    }
  }, [url, value]);

  useEffect(() => {
    fetchData();
  }, [url, value]);

  return (
    <BlockUi tag="div" blocking={blocking}>
      <div
        ref={setContainerEl}
        className="input-group"
        style={{ position: 'relative', cursor: 'pointer' }}
        onClick={() => selectRef.current?.focus()}
      >
        <label style={{ marginLeft: '19px', cursor: 'pointer' }} htmlFor={id}>{label}</label>
        <Controller
          name={name}
          control={control}
          render={({ field }) => {
            const selected = options.find(o => o.value == field.value) || null;
            return (
              <Select
                ref={selectRef}
                inputId={id}
                options={options}
                value={selected}
                onChange={option => {
                  const val = option ? option.value : null;
                  field.onChange(val);
                  onChangeProp({ target: { value: val } });
                }}
                placeholder={placeholder}
                isClearable={isClearable}
                openMenuOnFocus
                menuPortalTarget={containerEl}
                menuPosition="absolute"
                styles={selectStyles}
                className="flex-fill"
                noOptionsMessage={() => 'Sin opciones'}
              />
            );
          }}
        />
      </div>
      {error && (
        <div className="invalid-feedback" style={{ display: 'block', marginLeft: '19px' }}>
          {error.message}
        </div>
      )}
    </BlockUi>
  );
};

export default FormSelectApiSearchable;
